/* ProgressToast — slide-up progress indicator for long-running batch jobs.
   Ported from lib/progress-toast.jsx (claude_design project 5c0836b6-facf-49dd-9c78-62afe3f7bc12). */

import React from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export type ProgressStatus = 'running' | 'success' | 'partial' | 'error';

export interface ProgressItem {
  icon?: IconName;
  count: number;
  label: string;
  warn?: boolean;
}

export interface ProgressToastProps {
  open: boolean;
  title?: string;
  subtitle?: string;
  items?: ProgressItem[];
  status?: ProgressStatus;
  progress?: number;
  onClose?: () => void;
}

interface StatusHead {
  color: string;
  icon: IconName | null;
  statusLabel: string;
}

const STATUS_HEAD: Record<ProgressStatus, StatusHead> = {
  running: { color: 'accent', icon: null, statusLabel: 'Working…' },
  success: { color: 'green', icon: 'check-circle', statusLabel: 'Complete' },
  partial: {
    color: 'amber',
    icon: 'alert-triangle',
    statusLabel: 'Completed with warnings',
  },
  error: { color: 'red', icon: 'x-circle', statusLabel: 'Failed' },
};

const BAR_COLOR: Record<ProgressStatus, string> = {
  running: 'var(--accent)',
  success: 'var(--green-solid)',
  partial: 'var(--amber-solid)',
  error: 'var(--red-solid)',
};

export function ProgressToast({
  open,
  title,
  subtitle,
  items = [],
  status = 'running',
  progress = 0,
  onClose,
}: ProgressToastProps) {
  if (!open) return null;

  const { color, icon, statusLabel } =
    STATUS_HEAD[status] ?? STATUS_HEAD.running;
  const barColor = BAR_COLOR[status];

  return (
    <div className="progress-toast">
      <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
        <span
          style={{
            marginTop: 1,
            color: `var(--${color}-solid, var(--accent))`,
            flexShrink: 0,
          }}
        >
          {status === 'running' ? (
            <span className="spinner sm" />
          ) : (
            icon && <Icon name={icon} size={18} />
          )}
        </span>

        <div className="grow" style={{ minWidth: 0 }}>
          <div
            className="row"
            style={{ justifyContent: 'space-between', gap: 8 }}
          >
            <span style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>
              {title}
            </span>
            <span
              style={{
                fontSize: 'var(--fs-2xs)',
                fontWeight: 600,
                color: `var(--${color}-text, var(--text-muted))`,
              }}
            >
              {statusLabel}
            </span>
          </div>
          {subtitle && (
            <div
              className="text-muted"
              style={{ fontSize: 'var(--fs-xs)', marginTop: 2 }}
            >
              {subtitle}
            </div>
          )}
        </div>

        {status !== 'running' && onClose && (
          <button
            className="btn btn-ghost btn-icon btn-sm"
            style={{ height: 22, width: 22, marginTop: -2 }}
            onClick={onClose}
            aria-label="Dismiss"
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="progress-track" style={{ marginTop: 12 }}>
        <div
          className="progress-fill"
          style={{
            width: Math.max(4, Math.min(100, progress)) + '%',
            background: barColor,
          }}
        />
      </div>

      {/* Item counts */}
      {items.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 16px',
            marginTop: 11,
          }}
        >
          {items.map((it, idx) => (
            <div
              key={idx}
              className="row gap-2"
              style={{ fontSize: 'var(--fs-xs)' }}
            >
              {it.icon && (
                <Icon
                  name={it.icon}
                  size={13}
                  style={{
                    color: it.warn ? 'var(--amber-text)' : 'var(--text-muted)',
                  }}
                />
              )}
              <span
                className="mono"
                style={{
                  fontWeight: 700,
                  color:
                    it.warn && it.count > 0
                      ? 'var(--amber-text)'
                      : 'var(--text)',
                }}
              >
                {it.count}
              </span>
              <span className="text-muted">{it.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProgressToast;
